"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";

export type SalaryPageType = "guide" | "role" | "vs" | "tertinggi";

type Props = {
	locale: string;
	pageType: SalaryPageType;
	/**
	 * Only set on the per-role page (`pageType: "role"`) — omitted elsewhere.
	 * Named `roleId`, not `role`, so a literal string value here doesn't trip
	 * Biome's `lint/a11y/useValidAriaRole` (it flags any JSX attribute named
	 * `role`, custom-component props included, when given a string literal).
	 * The emitted PostHog property is still `role` per the analytics contract.
	 */
	roleId?: string;
};

/**
 * Fires `salary_page_viewed` once on mount across the salary-content page
 * family (/salary-guide, /gaji, /gaji/[role], /gaji-vs/[pair],
 * /gaji-tertinggi). Mirrors LandingViewBeacon (src/components/homepage/
 * landing-view-beacon.tsx): these pages are force-static/ISR (deterministic,
 * user-agnostic HTML), so a server-side capture would only fire on cache
 * revalidation, not per visitor. Renders null.
 */
export function SalaryPageViewBeacon({ locale, pageType, roleId }: Props) {
	const posthog = usePostHog();
	useEffect(() => {
		posthog?.capture("salary_page_viewed", {
			page_type: pageType,
			...(roleId ? { role: roleId } : {}),
			locale,
		});
	}, [posthog, pageType, roleId, locale]);
	return null;
}
